import { useState, useEffect } from "react";
import { useHttpClient } from "../../../../shared/hooks/http-hook";
import FiltrosRelatorio from "../ui/filtrosRelatorio/FiltrosRelatorio";
import RelatorioDocumentosEletronicosComErro from "./components/RelatorioDocumentosEletronicosComErro";
import RelatorioDocumentosEletronicosEmitidos from "./components/RelatorioDocumentosEletronicosEmitidos";
import RelatorioDfesPorEmitir from "./components/relatorioDfesPorEmitir/RelatorioDfesPorEmitir";
import RelatorioConsultaDfes from "./components/relatorioConsultaDfes/RelatorioConsultaDfes";
import "./RelatoriosDocumentosEletronicos.scss";

import PdfFaturaWrapper from "../../../../shared/components/PdfGenerator/PdfFatura/PdfFaturaWrapper";
import { useLoading } from "../../../../shared/context/LoadingContext";

const tiposRelatorios = [
  { value: "", label: "Selecione um relatório" },
  { value: "erros", label: "Relatório de DFEs com erro" },
  { value: "consulta", label: "Consulta de Documentos Eletrónicos" },
  { value: "emitidos", label: "Relatório de DFEs emitidos" },
  { value: "dfesPorEmitir", label: "Relatório de DFEs por emitir" },
  // Adicione mais tipos de relatórios de agendamento conforme necessário
];

// Definição de quais relatórios suportam quais ações
const relatorioCaps = {
  consulta: { gerar: true, exportar: false },
  dfesPorEmitir: { gerar: true, exportar: true },
  emitidos: { gerar: true, exportar: true },
  erros: { gerar: true, exportar: true },
  // Configure as capacidades para outros relatórios aqui
};

function RelatoriosDocumentosEletronicos({ auth, clinica }) {
  const { startLoading, stopLoading } = useLoading();
  const { sendRequest } = useHttpClient();
  const [dadosClinica, setDadosClinica] = useState();
  // Unidades da clínica e a escolhida. Vazio = consolidado da empresa; em
  // clínicas de unidade única a lista fica vazia e o seletor nem aparece.
  const [unidades, setUnidades] = useState([]);
  const [unidadeSelecionada, setUnidadeSelecionada] = useState("");
  const [dadosRelatorio, setDadosRelatorio] = useState({
    open: "",
    dataFiltro: {},
    acao: "",
    //Contador para forçar a atualização
    atualizacoes: 0,
  });

  const [dadosFatura, setDadosFatura] = useState({ show: false });

  useEffect(() => {
    const fetchDadosClinica = async () => {
      startLoading();
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/clinicas/${clinica.clinica._id}`,
          "GET",
          null,
          {
            Authorization: "Bearer " + auth.token,
          },
        );
        setDadosClinica(responseData.clinica);

        // Só faz sentido oferecer a escolha quando a clínica tem mesmo mais do
        // que uma unidade — nas outras nem se pede a lista.
        if (responseData.clinica?.multiEstabelecimento) {
          const resUnidades = await sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/estabelecimentos/clinica/${clinica.clinica._id}`,
            "GET",
            null,
            { Authorization: "Bearer " + auth.token },
          );
          setUnidades(resUnidades.estabelecimentos || []);
        }
      } catch (err) {
        console.error("err", err);
      } finally {
        stopLoading();
      }
    };
    fetchDadosClinica();
  }, [auth.perm, auth.token, sendRequest]);

  const gerarRelatorioHandler = (dataInicial, dataFinal, tipoRelatorio) => {
    if (!tipoRelatorio) {
      // Alerta se nenhum tipo de relatório for selecionado
      return;
    }

    setDadosRelatorio((prevState) => ({
      open: tipoRelatorio,
      dataFiltro: { dataInicial: dataInicial, dataFinal: dataFinal },
      acao: "gerar",
      atualizacoes: prevState.atualizacoes + 1,
    }));
  };

  const exportarFicheiroHandler = (dataInicial, dataFinal, tipoRelatorio) => {
    if (!tipoRelatorio) {
      // Alerta se nenhum tipo de relatório for selecionado
      return;
    }

    setDadosRelatorio((prevState) => ({
      open: tipoRelatorio,
      dataFiltro: { dataInicial: dataInicial, dataFinal: dataFinal },
      acao: "exportar",
      atualizacoes: prevState.atualizacoes + 1,
    }));
  };

  // Função auxiliar para renderizar o componente de relatório correto
  const renderizarRelatorio = () => {
    const { open, dataFiltro, acao, atualizacoes } = dadosRelatorio;

    if (!open) return null;

    const props = {
      dataFiltro: dataFiltro,
      dadosClinica: dadosClinica,
      auth: auth,
      acao: acao,
      atualizacoes: atualizacoes,
      showFatura: showFatura,
      // Unidade escolhida (vazio = consolidado) e a lista, para o relatório
      // filtrar e identificar de que unidade são os dados.
      unidadeSelecionada: unidadeSelecionada,
      unidades: unidades,
    };

    switch (open) {
      // case "erros":
      //   return <RelatorioDocumentosEletronicosComErro {...props} />;
      case "consulta":
        return <RelatorioConsultaDfes {...props} />;
      case "emitidos":
        return <RelatorioDocumentosEletronicosEmitidos {...props} />;
      case "dfesPorEmitir":
        return <RelatorioDfesPorEmitir {...props} />;
      case "erros":
        return <RelatorioDocumentosEletronicosComErro {...props} />;
      // Adicione outros casos conforme necessário
      default:
        return null;
    }
  };

  const showFatura = (dadosFatura) => {
    setDadosFatura(dadosFatura);
  };

  const showPreviousPage = () => {
    if (dadosFatura.show) {
      setDadosFatura({ show: false });
    }
  };

  return (
    <div>
      {dadosFatura.show && (
        <PdfFaturaWrapper
          showPreviousPage={showPreviousPage}
          faturaData={dadosFatura}
        />
      )}

      <FiltrosRelatorio
        tiposRelatorios={tiposRelatorios}
        gerarRelatorioHandler={gerarRelatorioHandler}
        exportarFicheiroHandler={exportarFicheiroHandler}
        relatorioCaps={relatorioCaps}
        // Manter o tipo de relatório selecionado
        relatorioSelecionado={dadosRelatorio.open}
        unidades={unidades}
        unidadeSelecionada={unidadeSelecionada}
        onUnidadeChange={setUnidadeSelecionada}
        relatoriosComUnidade={[
          "consulta",
          "emitidos",
          "erros",
          "dfesPorEmitir",
        ]}
      />

      {renderizarRelatorio()}
    </div>
  );
}

export default RelatoriosDocumentosEletronicos;
